/**
 * @file password-input.tsx
 * @description Reusable password input with visibility toggle (Eye/EyeOff).
 * @author William Donaldson
 * @last_modified 2026-03-02
 */
"use client";

import { Eye, EyeOff } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

import { type InputProps } from "./input";

const PasswordInput = React.forwardRef<HTMLInputElement, InputProps>(
  // WHY strip type: callers may pass type="password" which would override
  // the internal toggle. Destructuring it out ensures the toggle always wins.
  ({ className, error, type: _type, ...props }, ref) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const isInert = props.disabled || props.readOnly;

    return (
      <div className="relative">
        <input
          {...props}
          type={showPassword ? "text" : "password"}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-transparent px-3 py-2 pr-10 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
            error && "border-destructive focus-visible:ring-destructive",
            className
          )}
          ref={ref}
        />
        <button
          type="button"
          onClick={() => setShowPassword((prev) => !prev)}
          disabled={isInert}
          aria-label={showPassword ? "Hide password" : "Show password"}
          aria-pressed={showPassword}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm disabled:pointer-events-none disabled:opacity-50"
        >
          {showPassword ? (
            <EyeOff className="h-5 w-5" />
          ) : (
            <Eye className="h-5 w-5" />
          )}
        </button>
      </div>
    );
  }
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
